// @flow

import { Component } from '../jsti/core';
import Notifications from '../constants/Notifications';
import ProductStatusClasses from '../constants/ProductStatusClasses';
import AsyncRequest from '../classes/AsyncRequest';
import { isEqual, price, subscriptionPeriodTranslation } from '../classes/Helpers';
import ProductStatus from '../constants/ProductStatus';
import Product from '../constants/Product';
import Icon from '../classes/Icon';
import EventNames from '../constants/EventNames';
import DataLayerEventNames from '../constants/DataLayer/EventNames';
import type { CarouselImage } from '../interfaces/Image';

type PromotionLabel = {
  label: string,
  title: string,
  description: string
}

export default class ProductSelector extends Component {
  sku: string;
  price: HTMLElement;
  priceOriginal: HTMLElement;
  status: HTMLElement;
  amount: HTMLSelectElement;
  addToCartBtn: HTMLButtonElement;
  activeProduct: Object = {};
  products: Array<Object> = [];
  subscription: ?HTMLElement;
  subscriptionOrder: ?HTMLElement;
  regularOrder: ?HTMLElement;
  subscriptionFrequency: ?HTMLElement;
  changeSubscriptionHandler: function;
  unitPrice: ?HTMLElement;
  unavailableProductSku: ?HTMLInputElement;
  subscriptionPeriods: Array;
  subscriptionDefault: string;

  /**
   * Instantiate the component.
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);

    this.amount = this.el.getElementByRef('select');
    this.price = this.el.getElementByRef('price');
    this.priceOriginal = this.el.getElementByRef('price-original');
    this.unitPrice = this.el.getElementByRef('unit-price');
    this.status = this.el.getElementByRef('status');
    this.addToCartBtn = this.el.getElementByRef('add-to-cart');
    this.subscription = this.el.getElementByRef('subscription');

    if (!window.locale || !window.currency || !window.productSku || !window.variants) {
      throw new Error('All window variables are required');
    }

    this.sku = window.productSku;
    this.products = window.variants;
    this.activeProduct = window.activeVariant;

    this.addListener(Notifications.SELECT_PRODUCT, this.handleSelectProduct);
    this.addListener(Notifications.CHANGE_PRODUCT, this.handleChangeProduct);
    this.addListener(Notifications.UPDATE_PRODUCT_AMOUNT, this.onAmountUpdate);

    this.changeSubscriptionHandler = this.changeSubscription.bind(this);

    if (this.subscription) {
      this.subscriptionOrder = this.el.getElementByRef('subscription-order');
      this.regularOrder = this.el.getElementByRef('regular-order');
      this.subscriptionFrequency = this.el.getElementByRef('subscription-frequency');

      this.subscriptionOrder.addEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);
      this.regularOrder.addEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);
      this.subscriptionFrequency.addEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);

      if (this.el.dataset.subscriptionPeriods && this.el.dataset.subscriptionDefault) {
        this.subscriptionPeriods = JSON.parse(this.el.dataset.subscriptionPeriods);
        this.subscriptionDefault = Number(this.el.dataset.subscriptionDefault);
      }
    }
  }

  /**
   * Executes when a new variant is chosen
   * When the sku of the product is given, automatically select the product by filters
   * Otherwise look up the product by the given identifiers
   */
  handleSelectProduct(data: Object): void {
    const product: Object = data.sku
      ? this.products.find((product: Object) => data.sku === product.sku)
      : this.getChosenProduct(data);

    this.notify(Notifications.CHANGE_PRODUCT, {
      product,
      allProducts: this.products
    });
  }

  /**
   * Executes when the new active variant is known.
   *
   * @param pData
   */
  handleChangeProduct(pData: Object): void {
    this.activeProduct = pData.product;
    this.updateActiveProductDatalayer(this.activeProduct);

    if (pData.product.status_code === ProductStatus.END_OF_SALE && pData.product.stock <= 0) {
      this.notify(Notifications.HIDE_PRODUCT_ORDERABLE);
    } else {
      this.notify(Notifications.SHOW_PRODUCT_ORDERABLE);
    }

    this.price = this.el.getElementByRef('price');

    if (this.price) {
      this.price.innerText = price(this.activeProduct.price);
    }

    this.unitPrice = this.el.getElementByRef('unit-price');

    if (this.unitPrice) {
      if (this.activeProduct.price_unit && this.activeProduct.unit) {
        this.unitPrice.innerText = `(${price(this.activeProduct.price_unit)} / ${this.activeProduct.unit})`;
      } else {
        this.unitPrice.innerText = '';
      }
    }

    this.unavailableProductSku = this.el.getElementByRef('unavailable-product-sku');
    if (this.unavailableProductSku) {
      this.unavailableProductSku.value = this.activeProduct.sku;
    }

    if (pData.product.status_code === ProductStatus.END_OF_SALE && pData.product.stock <= 0) {
      return;
    }

    if (!this.priceOriginal) {
      this.priceOriginal = this.el.getElementByRef('price-original');
    }

    if (!this.amount) {
      this.amount = this.el.getElementByRef('select');
    }
    this.updateProductAmountSelect(this.amount, pData.product.stock, pData.product.status_code);

    if (!this.status) {
      this.status = this.el.getElementByRef('status');
    }

    if (!this.addToCartBtn) {
      this.addToCartBtn = this.el.getElementByRef('add-to-cart');
    }

    const product: Object = pData.product;

    this.amount.value = '1';
    this.addToCartBtn.dataset.sku = product.sku;
    this.notify(Notifications.PRODUCT_SKU_CHANGED, { sku: product.sku });

    if (product.price_original) {
      this.priceOriginal.innerText = price(product.price_original);
      this.priceOriginal.classList.remove('hidden');
    } else {
      this.priceOriginal.classList.add('hidden');
    }

    this.updateProductStatus(
      product.stock,
      product.in_stock_delivery_date,
      product.out_of_stock_delivery_date,
      product.amount,
      product.status_code);

    if (!product.promotionLabel) {
      if (product.status_code === ProductStatus.END_OF_LIFE && product.stock < 10) {
        product.promotionLabel = { label: window.store.getTranslation('product.last.chance') };
      }
    }
    this.updatePromotion(product.promotionLabel);

    this.updateSubscription();
    this.updateSubscriptionLabel(product.subscription);
  }

  /**
   * Executes when the selected amount of the variant is updated.
   *
   * @param data
   * @param data.amount
   */
  onAmountUpdate({ amount }: Object): void {
    this.updatePrice(amount);
    this.updateProductStatus(
      this.activeProduct.stock,
      this.activeProduct.in_stock_delivery_date,
      this.activeProduct.out_of_stock_delivery_date,
      amount
    );
  }

  /**
   * Updates the price to the price of the current variant.
   *
   * @param amount - {number}.
   */
  updatePrice(amount: number): void {
    this.price.innerText = price(this.activeProduct.price);
  }

  /**
   * Get the correct variant according to the data received.
   *
   * @param pObject - {object}.
   * @returns {object} .
   */
  getChosenProduct(pObject: Object): Object {
    const identifiers: Object = {};
    for (const x in this.activeProduct.identifiers) {
      identifiers[x] = this.activeProduct.identifiers[x];
    }
    identifiers[pObject.name] = pObject.id;

    // Find product that exactly matches the identifiers
    let product: Object | undefined = this.products.find((product: Object) => {
      return isEqual(product.identifiers, identifiers);
    });

    // If no match was found, find the first product with the latest selected identifier
    if (!product) {
      product = this.products.find((product: Object) => {
        return product.identifiers[pObject.name] === pObject.id;
      });
    }

    return product;
  }

  /**
   * Retrieve the product status.
   *
   * @param stock
   * @param inStockDeliveryDate
   * @param outOfStockDeliveryDate
   * @param amount
   * @param productStatus
   */
  updateProductStatus(
    stock: number,
    inStockDeliveryDate: string,
    outOfStockDeliveryDate: string,
    amount: number,
    productStatus: string
  ): void {
    AsyncRequest.post('', {
      products: [
        {
          stock,
          inStockDeliveryDate,
          outOfStockDeliveryDate,
          amount,
          productStatus
        }
      ]
    }, 'product-statuses')
      .then(({ data }: Object) => {
        if (!data[0].isTest) {
          this.status.innerHTML = `
          ${new Icon({ icon: data[0].icon, width: 12 }).generate().outerHTML}
          ${data[0].text}
        `;

          Object.values(ProductStatusClasses).forEach((status: any) => this.status.classList.remove(status));
          this.status.classList.add(data[0].class);
        } else if (data[0].isTest === 'B') {
          this.status.innerHTML = `
          <p class="flex items-center gap-2 mb-2 w-fit rounded-full py-1 px-4 ${data[0].bold ? 'font-bold' : ''} ${data[0].bgcolor}">
          <span class="${data[0].iconClass}">
                ${new Icon({ icon: data[0].testIcon, width: 16 }).generate().outerHTML}
            </span>
            <span class="${data[0].titleClass}">
                ${data[0].title}
            </span>
          </p>
          <p class="mt-0 ${data[0].testClass} ${data[0].showText ? '' : 'hidden'}">
            ${data[0].text}
          </p>
        `;
        } else if (data[0].isTest === 'C') {
          this.status.innerHTML = `
          <p class="flex items-center gap-2 mb-2 w-fit rounded-full py-1 px-4 border-2 border-solid ${data[0].bold ? 'font-bold' : ''}  ${data[0].borderColor}">
          <span class="${data[0].iconClass}">
                ${new Icon({ icon: data[0].testIcon, width: 16 }).generate().outerHTML}
            </span>
            <span class="${data[0].titleClass}">
                ${data[0].title}
            </span>
          </p>
          <p class="mt-0 ${data[0].testClass} ${data[0].showText ? '' : 'hidden'}">
            ${data[0].text}
          </p>
        `;
        } else {
          this.status.innerHTML = `
          <p class="flex items-center gap-2 mb-2 ${data[0].bold ? 'font-bold' : ''} ">
          <span class="${data[0].iconClass}">
                ${new Icon({ icon: data[0].testIcon, width: 16 }).generate().outerHTML}
            </span>
            <span class="${data[0].titleClass}">
                ${data[0].title}
            </span>
          </p>
          <p class="mt-0 ${data[0].testClass} ${data[0].showText ? '' : 'hidden'}">
            ${data[0].text}
          </p>
        `;
        }
      })
      .catch(() => {
        console.warn(`${this.name}: Could not retrieve the product status`);
      });
  }

  /**
   *
   */
  updatePromotion(label: ?PromotionLabel) {
    if (!label) {
      this.notify(Notifications.HIDE_PROMOTION);
      return;
    }

    this.notify(Notifications.SHOW_PROMOTION, { label: label.label });

    const promotionLabel = document.getElementByRef('promotion-label');
    const promotionTitle = document.getElementByRef('promotion-title');
    const promotionDescription = document.getElementByRef('promotion-description');

    if (promotionLabel && label.label) {
      promotionLabel.innerHTML = label.label;
    }
    if (promotionTitle && label.title) {
      promotionTitle.innerHTML = label.title;
    }
    if (promotionDescription && label.description) {
      promotionDescription.innerHTML = label.description;
    }
  }

  updateSubscriptionLabel(subscription: ?Object) {
    if (!subscription) {
      this.notify(Notifications.HIDE_REPEAT_BANNER);
      return;
    }

    this.notify(Notifications.SHOW_REPEAT_BANNER);
  }

  /**
   * updates the product amount selector
   */
  updateProductAmountSelect(select: HTMLElement, stock: number, status: number) {
    while (select.firstChild) {
      select.firstChild.remove();
    }
    let items = Product.MAX_AMOUNT_SELECTOR;
    if (stock < Product.MAX_AMOUNT_SELECTOR) {
      if (status === ProductStatus.END_OF_SALE || status === ProductStatus.END_OF_LIFE) {
        items = stock;
      }
    }

    for (let i = 1; i < items + 1; i++) {
      const option = document.createElement('option');
      option.value = i.toString();
      option.innerText = i.toString();
      select.appendChild(option);
    }
  }

  /**
   *
   */
  updateSubscription() {
    if (!this.subscription) return;
    if (!this.activeProduct.subscription) {
      this.price.classList.remove('hidden');
      this.unitPrice.classList.remove('hidden');
      this.subscription.classList.add('hidden');
      this.addToCartBtn.removeAttribute('data-subscription-period');
      this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: null });

      if (!this.activeProduct.price_original) return;
      this.priceOriginal.classList.remove('hidden');
      return;
    }
    const defaultPrice = this.subscription.getElementByRef('default-price');
    const defaultOriginalPrice = this.subscription.getElementByRef('default-original-price');
    const originalPrice = this.subscription.getElementByRef('original-price');
    const discountPrice = this.subscription.getElementByRef('discount-price');
    const defaultUnitPrice = this.subscription.getElementByRef('default-unit-price');
    const discountUnitPrice = this.subscription.getElementByRef('discount-unit-price');
    const frequency = this.subscription.getElementByRef('subscription-frequency');

    frequency.innerHTML = '';
    defaultPrice.innerHTML = price(this.activeProduct.price);
    discountPrice.innerHTML = price(this.activeProduct.subscription.discount.price);
    if (discountUnitPrice) {
      if (this.activeProduct.subscription.discount.price_unit && this.activeProduct.unit) {
        discountUnitPrice.innerHTML = `(${price(this.activeProduct.subscription.discount.price_unit)} / ${this.activeProduct.unit})`;
      } else {
        discountUnitPrice.innerHTML = '';
      }
    }
    if (defaultUnitPrice) {
      if (this.activeProduct.price_unit && this.activeProduct.unit) {
        defaultUnitPrice.innerHTML = `(${price(this.activeProduct.price_unit)} / ${this.activeProduct.unit})`;
      } else {
        defaultUnitPrice.innerHTML = '';
      }
    }

    originalPrice.innerHTML = price(this.activeProduct.price_original || this.activeProduct.price);

    if (this.activeProduct.price_original) {
      defaultOriginalPrice.innerHTML = price(this.activeProduct.price_original);
      defaultOriginalPrice.classList.remove('hidden');
    } else {
      defaultOriginalPrice.classList.add('hidden');
    }

    this.price.classList.add('hidden');
    this.priceOriginal.classList.add('hidden');
    this.unitPrice.classList.add('hidden');
    this.subscription.classList.remove('hidden');

    this.addToCartBtn.dataset.subscriptionPeriod = this.subscriptionDefault;
    this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: this.subscriptionDefault });

    this.subscriptionPeriods.forEach((period: number) => {
      const option = document.createElement('option');
      option.value = period.toString();

      option.innerText = subscriptionPeriodTranslation(period, this.activeProduct.subscription.frequency.id);
      if (this.subscriptionDefault === period) {
        option.selected = true;
      }
      frequency.appendChild(option);
    });

    if (this.subscriptionOrder.checked) {
      this.addToCartBtn.dataset.subscriptionPeriod = this.subscriptionFrequency.value;
      this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: this.subscriptionFrequency.value });
    } else {
      this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: null });
    }
  }

  /**
   *
   */
  changeSubscription(event: Event) {
    if (event.target === this.subscriptionOrder || event.target === this.subscriptionFrequency) {
      this.addToCartBtn.dataset.subscriptionPeriod = this.subscriptionFrequency.value;
      this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: this.subscriptionFrequency.value });
    } else if (event.target === this.regularOrder) {
      this.addToCartBtn.dataset.subscriptionPeriod = null;
      this.notify(Notifications.UPDATE_SUBSCRIPTION_PERIOD, { subscriptionPeriod: null });
    }
  }

  /**
   *
   */
  updateActiveProductDatalayer(activeProduct: Object): void {
    const variantImage = window.store.carouselMedia.find(
      (image: CarouselImage) => image.productId === activeProduct.id
    );

    const activeVariant = {
      id: activeProduct.sku,
      groupId: activeProduct.groupId,
      name: activeProduct.name,
      taxonomy: activeProduct.category.split('/'),
      url: window.location.origin + window.location.pathname + '?sku=' + activeProduct.sku,
      currency: window.currency,
      unit_price: activeProduct.price_original ? +activeProduct.price_original : +activeProduct.price,
      unit_sale_price: +activeProduct.price,
      product_image_url: variantImage?.src ?? '',
      custom: activeProduct.custom ?? []
    };

    this.dispatch({
      event: DataLayerEventNames.UPDATE_INSIDER_ACTIVE_VARIANT,
      Insider: true,
      data: { activeVariant }
    });
  }

  /**
   *
   */
  destroy(): void {
    if (this.subscription) {
      this.subscriptionOrder.removeEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);
      this.regularOrder.removeEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);
      this.subscriptionFrequency.removeEventListener(EventNames.CHANGE, this.changeSubscriptionHandler);
    }
    super.destroy();
  }
}
